/**
 * useReviewConfig — small hook that fetches the review config +
 * category list once per admin session and exposes memoized helpers.
 *
 * Returns:
 *   { enabled, categories, loading, refresh, getCategory(id), reload }
 *
 * `enabled` is the *effective* flag for the current user (server-side
 * check, so sub-admin gating is honoured). `categories` is an array of
 * {id, name, color, description, usage_*} entries.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Shared in-memory cache so the many pages that need this don't each
// re-fetch. Refreshable via reload().
let _cache = null;
let _pending = null;

async function _fetch() {
  const [cfg, cats] = await Promise.all([
    axios.get(`${API}/api/admin/review/config`, { headers: authHeaders() }),
    axios.get(`${API}/api/admin/review/categories`, { headers: authHeaders() }).catch(() => ({ data: { items: [] } })),
  ]);
  return {
    enabled: !!cfg.data?.enabled_for_me,
    isFullAdmin: !!cfg.data?.is_full_admin,
    subAdminToggle: !!cfg.data?.sub_admin_toggle,
    categories: cats.data?.items || [],
  };
}

export function useReviewConfig() {
  const [state, setState] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      _cache = await _fetch();
      _pending = null;
      setState(_cache);
    } catch (e) {
      // 404 for non-admin visitors — return an empty disabled state.
      _cache = { enabled: false, isFullAdmin: false, subAdminToggle: false, categories: [] };
      setState(_cache);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (_cache) return;
    if (!_pending) _pending = _fetch().then((v) => { _cache = v; return v; }).catch(() => {
      _cache = { enabled: false, isFullAdmin: false, subAdminToggle: false, categories: [] };
      return _cache;
    });
    _pending.then((v) => { setState(v); setLoading(false); });
  }, []);

  const getCategory = useCallback((id) => (state?.categories || []).find((c) => c.id === id), [state]);

  return {
    enabled: !!state?.enabled,
    isFullAdmin: !!state?.isFullAdmin,
    subAdminToggle: !!state?.subAdminToggle,
    categories: state?.categories || [],
    loading,
    reload,
    getCategory,
  };
}

/** Force-invalidate the shared cache (used after category CRUD changes). */
export function invalidateReviewConfig() {
  _cache = null;
  _pending = null;
}

export default useReviewConfig;
